import { appAlert } from '../../services/appDialogs';
import React, { useState, useRef, useEffect, useCallback } from 'react';
import { fabric } from 'fabric';
import {
  X, Play, Pause, Square, Pencil, Eraser, MousePointer,
  Plus, Copy, Trash2, ChevronLeft, ChevronRight, Star,
  Circle, RectangleHorizontal, Minus, PaintBucket, PenLine,
} from 'lucide-react';
import { useTheme } from '../../context/ThemeContext';
import useFrameManager from '../../hooks/useFrameManager';
import { uploadFileMultipart } from '../../utils/fileUtils';
import { authenticatedFetch } from '../../utils/api';
import { API_HOST } from '../../utils/constants';
import { useDelayedUnmount } from '../../hooks/useDelayedUnmount';
import { ConfirmDialog } from '../ui/ConfirmDialog';

// 画布比例预设
const ASPECT_RATIOS = [
  { label: '16:9', w: 1920, h: 1080 },
  { label: '9:16', w: 1080, h: 1920 },
  { label: '4:3', w: 1440, h: 1080 },
  { label: '21:9', w: 2520, h: 1080 },
];

/**
 * 逐帧动画编辑器 - 全屏模式
 */
export default function MotionStoryboardEditor({ isOpen, nodeData, projectId, episodeId, onSave, onClose }) {
  const { getTheme } = useTheme();
  const theme = getTheme();

  // Canvas 相关
  const canvasContainerRef = useRef(null);
  const fabricCanvasRef = useRef(null);
  const onionCanvasRef = useRef(null);

  // 比例选择
  const initRatio = ASPECT_RATIOS.find(r => r.w === (nodeData?.width || 1920) && r.h === (nodeData?.height || 1080)) || ASPECT_RATIOS[0];
  const [aspectRatio, setAspectRatio] = useState(initRatio.label);
  const [width, setWidth] = useState(initRatio.w);
  const [height, setHeight] = useState(initRatio.h);
  const [fps] = useState(nodeData?.fps || 24);
  const [pendingAspectRatio, setPendingAspectRatio] = useState(null);

  // 工具状态
  const [tool, setTool] = useState('brush');
  // 功能色：画笔颜色为画布绘制内容色（色板见下），不接入主题变量
  const [brushColor, setBrushColor] = useState('#000000');
  const [brushSize, setBrushSize] = useState(5);
  const [brushOpacity, setBrushOpacity] = useState(1);

  // 形状绘制临时状态
  const shapeStartRef = useRef(null); // { x, y }
  const tempShapeRef = useRef(null);  // 正在绘制的临时形状对象

  // 播放状态
  const [isPlaying, setIsPlaying] = useState(false);
  const isPlayingRef = useRef(false);
  const playAnimationRef = useRef(null);

  // 导出状态
  const [isExporting, setIsExporting] = useState(false);
  const [exportProgress, setExportProgress] = useState(0);

  // 洋葱皮
  const [onionSkinEnabled, setOnionSkinEnabled] = useState(true);
  const onionSkinLayers = 2; // 前后各显示几帧

  // 帧管理
  const {
    frames,
    currentFrameIndex,
    switchToFrame,
    nextFrame,
    prevFrame,
    addBlankFrame,
    duplicateFrame,
    deleteFrame,
    toggleKeyFrame,
    saveCurrentFrame,
    getFirstFrameThumbnail,
    clearAllFrames,
    restoreFrames,
    exportFrames,
    MAX_FRAMES,
  } = useFrameManager(fabricCanvasRef, {
    fps,
    onionSkinUpdate: updateOnionSkin,
  });

  // 初始化时恢复已保存的帧数据
  const restoredRef = useRef(false);
  useEffect(() => {
    if (!isOpen || !fabricCanvasRef.current || restoredRef.current) return;
    if (nodeData?.savedFrames?.length > 0) {
      // 延迟一帧等 Fabric canvas 就绪
      setTimeout(() => {
        restoreFrames(nodeData.savedFrames);
        restoredRef.current = true;
      }, 100);
    }
    restoredRef.current = true;
  }, [isOpen, nodeData, restoreFrames]);

  const applyAspectRatio = useCallback((ratioLabel) => {
    const ratio = ASPECT_RATIOS.find(r => r.label === ratioLabel);
    if (!ratio) return;
    if (ratio.w === width && ratio.h === height) return;

    setAspectRatio(ratioLabel);
    setWidth(ratio.w);
    setHeight(ratio.h);
    clearAllFrames();
  }, [width, height, clearAllFrames]);

  // 切换画布比例
  const handleAspectRatioChange = useCallback((ratioLabel) => {
    const ratio = ASPECT_RATIOS.find(r => r.label === ratioLabel);
    if (!ratio || (ratio.w === width && ratio.h === height)) return;

    if (frames.length > 1) {
      setPendingAspectRatio(ratioLabel);
      return;
    }
    applyAspectRatio(ratioLabel);
  }, [width, height, frames.length, applyAspectRatio]);

  // 计算显示尺寸（适配屏幕）
  const getDisplaySize = useCallback(() => {
    const maxW = window.innerWidth * 0.6;
    const maxH = window.innerHeight * 0.65;
    const scale = Math.min(maxW / width, maxH / height, 1);
    return { w: Math.round(width * scale), h: Math.round(height * scale), scale };
  }, [width, height]);

  const [displaySize, setDisplaySize] = useState(() => getDisplaySize());

  // 初始化 Fabric Canvas
  useEffect(() => {
    if (!isOpen || !canvasContainerRef.current) return;

    // 清理容器中的旧元素
    const container = canvasContainerRef.current;
    while (container.firstChild) {
      container.removeChild(container.firstChild);
    }

    const canvasEl = document.createElement('canvas');
    canvasEl.id = 'motion-fabric-canvas';
    container.appendChild(canvasEl);

    const canvas = new fabric.Canvas(canvasEl, {
      width,
      height,
      // 功能色：画布"纸面"固定为白色（绘画内容语义色，导出帧底色），不接入主题变量
      backgroundColor: '#ffffff',
      isDrawingMode: true,
    });

    // 设置画笔
    canvas.freeDrawingBrush = new fabric.PencilBrush(canvas);
    canvas.freeDrawingBrush.width = brushSize;
    canvas.freeDrawingBrush.color = brushColor;

    fabricCanvasRef.current = canvas;

    return () => {
      canvas.dispose();
      fabricCanvasRef.current = null;
    };
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [isOpen, width, height]);

  // 响应窗口大小变化 + 画布尺寸变化
  useEffect(() => {
    setDisplaySize(getDisplaySize());
    const handleResize = () => setDisplaySize(getDisplaySize());
    window.addEventListener('resize', handleResize);
    return () => window.removeEventListener('resize', handleResize);
  }, [getDisplaySize]);

  // 清理形状绘制事件
  const cleanupShapeEvents = useCallback((canvas) => {
    canvas.off('mouse:down', canvas.__shapeMouseDown);
    canvas.off('mouse:move', canvas.__shapeMouseMove);
    canvas.off('mouse:up', canvas.__shapeMouseUp);
    canvas.__shapeMouseDown = null;
    canvas.__shapeMouseMove = null;
    canvas.__shapeMouseUp = null;
  }, []);

  // 绑定形状绘制事件（直线/矩形/圆形）
  const setupShapeEvents = useCallback((canvas, shapeType) => {
    cleanupShapeEvents(canvas);

    const onMouseDown = (opt) => {
      const pointer = canvas.getPointer(opt.e);
      shapeStartRef.current = { x: pointer.x, y: pointer.y };

      const commonProps = {
        stroke: brushColor,
        strokeWidth: brushSize,
        fill: shapeType === 'rect' || shapeType === 'ellipse' ? 'transparent' : undefined,
        opacity: brushOpacity,
        selectable: false,
        evented: false,
      };

      let shape;
      if (shapeType === 'line') {
        shape = new fabric.Line([pointer.x, pointer.y, pointer.x, pointer.y], commonProps);
      } else if (shapeType === 'rect') {
        shape = new fabric.Rect({ left: pointer.x, top: pointer.y, width: 0, height: 0, ...commonProps });
      } else if (shapeType === 'ellipse') {
        shape = new fabric.Ellipse({ left: pointer.x, top: pointer.y, rx: 0, ry: 0, ...commonProps });
      }

      if (shape) {
        canvas.add(shape);
        tempShapeRef.current = shape;
      }
    };

    const onMouseMove = (opt) => {
      if (!shapeStartRef.current || !tempShapeRef.current) return;
      const pointer = canvas.getPointer(opt.e);
      const start = shapeStartRef.current;
      const shape = tempShapeRef.current;

      if (shapeType === 'line') {
        shape.set({ x2: pointer.x, y2: pointer.y });
      } else if (shapeType === 'rect') {
        const left = Math.min(start.x, pointer.x);
        const top = Math.min(start.y, pointer.y);
        shape.set({ left, top, width: Math.abs(pointer.x - start.x), height: Math.abs(pointer.y - start.y) });
      } else if (shapeType === 'ellipse') {
        const rx = Math.abs(pointer.x - start.x) / 2;
        const ry = Math.abs(pointer.y - start.y) / 2;
        shape.set({
          left: Math.min(start.x, pointer.x),
          top: Math.min(start.y, pointer.y),
          rx, ry,
        });
      }
      canvas.renderAll();
    };

    const onMouseUp = () => {
      if (tempShapeRef.current) {
        tempShapeRef.current.set({ selectable: true, evented: true });
        canvas.setActiveObject(tempShapeRef.current);
        canvas.renderAll();
      }
      shapeStartRef.current = null;
      tempShapeRef.current = null;
    };

    canvas.__shapeMouseDown = onMouseDown;
    canvas.__shapeMouseMove = onMouseMove;
    canvas.__shapeMouseUp = onMouseUp;
    canvas.on('mouse:down', onMouseDown);
    canvas.on('mouse:move', onMouseMove);
    canvas.on('mouse:up', onMouseUp);
  }, [brushColor, brushSize, brushOpacity, cleanupShapeEvents]);

  // 油漆桶填充
  const setupFillEvent = useCallback((canvas) => {
    cleanupShapeEvents(canvas);

    const onMouseDown = (opt) => {
      const target = canvas.findTarget(opt.e);
      if (target) {
        target.set({ fill: brushColor, opacity: brushOpacity });
        canvas.renderAll();
      }
    };

    canvas.__shapeMouseDown = onMouseDown;
    canvas.on('mouse:down', onMouseDown);
  }, [brushColor, brushOpacity, cleanupShapeEvents]);

  // 工具切换
  const handleToolChange = useCallback((newTool) => {
    const canvas = fabricCanvasRef.current;
    if (!canvas) return;

    setTool(newTool);
    cleanupShapeEvents(canvas);

    const brushTools = ['brush', 'pencil', 'eraser'];
    const shapeTools = ['line', 'rect', 'ellipse'];

    if (brushTools.includes(newTool)) {
      canvas.isDrawingMode = true;
      canvas.selection = false;

      switch (newTool) {
        case 'brush':
          canvas.freeDrawingBrush = new fabric.PencilBrush(canvas);
          canvas.freeDrawingBrush.width = brushSize;
          canvas.freeDrawingBrush.color = brushColor;
          break;
        case 'pencil':
          // 铅笔质感：细线条 + 圆角端点 + 抖动
          canvas.freeDrawingBrush = new fabric.PencilBrush(canvas);
          canvas.freeDrawingBrush.width = Math.max(brushSize * 0.7, 2);
          canvas.freeDrawingBrush.color = brushColor;
          canvas.freeDrawingBrush.strokeLineCap = 'round';
          canvas.freeDrawingBrush.strokeLineJoin = 'round';
          canvas.freeDrawingBrush.decimate = 2; // 抖动效果
          break;
        case 'eraser':
          if (fabric.EraserBrush) {
            canvas.freeDrawingBrush = new fabric.EraserBrush(canvas);
            canvas.freeDrawingBrush.width = brushSize * 2;
          } else {
            canvas.freeDrawingBrush = new fabric.PencilBrush(canvas);
            canvas.freeDrawingBrush.width = brushSize * 2;
            // 功能色：橡皮擦回退实现 = 用纸面白色涂抹（与画布背景一致），不接入主题变量
            canvas.freeDrawingBrush.color = '#ffffff';
          }
          break;
        default: break;
      }
      if (canvas.freeDrawingBrush && newTool !== 'eraser') {
        canvas.freeDrawingBrush.opacity = brushOpacity;
      }
    } else if (shapeTools.includes(newTool)) {
      canvas.isDrawingMode = false;
      canvas.selection = false;
      setupShapeEvents(canvas, newTool);
    } else if (newTool === 'fill') {
      canvas.isDrawingMode = false;
      canvas.selection = false;
      setupFillEvent(canvas);
    } else if (newTool === 'select') {
      canvas.isDrawingMode = false;
      canvas.selection = true;
    }
  }, [brushSize, brushColor, brushOpacity, cleanupShapeEvents, setupShapeEvents, setupFillEvent]);

  // 画笔属性变化
  useEffect(() => {
    const canvas = fabricCanvasRef.current;
    if (!canvas) return;

    const brushTools = ['brush', 'pencil'];
    if (brushTools.includes(tool) && canvas.freeDrawingBrush) {
      if (tool === 'pencil') {
        canvas.freeDrawingBrush.width = Math.max(brushSize * 0.7, 2);
      } else {
        canvas.freeDrawingBrush.width = brushSize;
      }
      canvas.freeDrawingBrush.color = brushColor;
      if (canvas.freeDrawingBrush.opacity !== undefined) {
        canvas.freeDrawingBrush.opacity = brushOpacity;
      }
    }
    if (tool === 'eraser' && canvas.freeDrawingBrush) {
      canvas.freeDrawingBrush.width = brushSize * 2;
    }
  }, [brushSize, brushColor, brushOpacity, tool]);

  // 洋葱皮更新
  function updateOnionSkin(currentIndex, framesSnapshot) {
    if (!onionSkinEnabled || !onionCanvasRef.current) return;

    const onionCanvas = onionCanvasRef.current;
    const ctx = onionCanvas.getContext('2d');
    ctx.clearRect(0, 0, width, height);

    // 绘制前 N 帧（红色调）
    for (let i = 1; i <= onionSkinLayers; i++) {
      const prevIndex = currentIndex - i;
      if (prevIndex >= 0 && framesSnapshot[prevIndex]?.thumbnail) {
        const img = new Image();
        img.src = framesSnapshot[prevIndex].thumbnail;
        img.onload = () => {
          ctx.save();
          ctx.globalAlpha = 0.3 / i;
          ctx.filter = 'sepia(1) hue-rotate(-30deg)';
          ctx.drawImage(img, 0, 0, width, height);
          ctx.restore();
        };
      }
    }

    // 绘制后 N 帧（蓝色调）
    for (let i = 1; i <= onionSkinLayers; i++) {
      const nextIndex = currentIndex + i;
      if (nextIndex < framesSnapshot.length && framesSnapshot[nextIndex]?.thumbnail) {
        const img = new Image();
        img.src = framesSnapshot[nextIndex].thumbnail;
        img.onload = () => {
          ctx.save();
          ctx.globalAlpha = 0.3 / i;
          ctx.filter = 'sepia(1) hue-rotate(180deg)';
          ctx.drawImage(img, 0, 0, width, height);
          ctx.restore();
        };
      }
    }
  }

  // 播放动画
  const handlePlay = useCallback(() => {
    if (isPlaying) return;

    setIsPlaying(true);
    isPlayingRef.current = true;

    const frameInterval = 1000 / fps;
    let lastTime = performance.now();
    let frameIdx = currentFrameIndex;

    const animate = (currentTime) => {
      if (!isPlayingRef.current) return;

      const elapsed = currentTime - lastTime;
      if (elapsed >= frameInterval) {
        frameIdx = (frameIdx + 1) % frames.length;
        switchToFrame(frameIdx);
        lastTime = currentTime;
      }

      playAnimationRef.current = requestAnimationFrame(animate);
    };

    playAnimationRef.current = requestAnimationFrame(animate);
  }, [isPlaying, fps, currentFrameIndex, frames.length, switchToFrame]);

  // 暂停播放
  const handlePause = useCallback(() => {
    setIsPlaying(false);
    isPlayingRef.current = false;
    if (playAnimationRef.current) {
      cancelAnimationFrame(playAnimationRef.current);
      playAnimationRef.current = null;
    }
  }, []);

  // 停止播放
  const handleStop = useCallback(() => {
    handlePause();
    switchToFrame(0);
  }, [handlePause, switchToFrame]);

  // 导出视频
  const handleExportVideo = useCallback(async () => {
    if (isExporting) return;

    setIsExporting(true);
    setExportProgress(0);

    try {
      // 保存当前帧（同步写入 ref）
      saveCurrentFrame();

      // 创建离屏 Canvas（原生，不用 Fabric）
      const offscreen = document.createElement('canvas');
      offscreen.width = width;
      offscreen.height = height;
      const ctx = offscreen.getContext('2d');

      // 先收集所有帧的完整图像（从 Fabric Canvas 逐帧导出）
      const canvas = fabricCanvasRef.current;
      if (!canvas) throw new Error('Canvas 未初始化');

      const currentIdx = currentFrameIndex;
      const frameImages = [];

      for (let i = 0; i < frames.length; i++) {
        // 切换到该帧
        canvas.clear();
        canvas.backgroundColor = '#ffffff'; // 功能色：导出帧纸面白，同上
        if (frames[i].fabricJson) {
          await new Promise(resolve => {
            canvas.loadFromJSON(frames[i].fabricJson, () => {
              canvas.renderAll();
              resolve();
            });
          });
        } else {
          canvas.renderAll();
        }
        // 导出为 dataURL
        frameImages.push(canvas.toDataURL({ format: 'png' }));
        setExportProgress(Math.round(((i + 1) / frames.length) * 50));
      }

      // 恢复到当前帧
      canvas.clear();
      canvas.backgroundColor = '#ffffff';
      if (frames[currentIdx]?.fabricJson) {
        canvas.loadFromJSON(frames[currentIdx].fabricJson, () => canvas.renderAll());
      } else {
        canvas.renderAll();
      }

      // 准备录制
      const stream = offscreen.captureStream(fps);
      const mimeType = MediaRecorder.isTypeSupported('video/webm;codecs=vp9')
        ? 'video/webm;codecs=vp9'
        : 'video/webm';
      const mediaRecorder = new MediaRecorder(stream, {
        mimeType,
        videoBitsPerSecond: 5000000,
      });

      const chunks = [];
      mediaRecorder.ondataavailable = (e) => {
        if (e.data.size > 0) chunks.push(e.data);
      };

      mediaRecorder.start();

      // 逐帧绘制到离屏 Canvas
      for (let i = 0; i < frameImages.length; i++) {
        const img = await loadImage(frameImages[i]);
        ctx.clearRect(0, 0, width, height);
        ctx.fillStyle = '#ffffff';
        ctx.fillRect(0, 0, width, height);
        ctx.drawImage(img, 0, 0, width, height);

        setExportProgress(50 + Math.round(((i + 1) / frameImages.length) * 45));
        await new Promise(r => setTimeout(r, 1000 / fps));
      }

      // 停止录制
      mediaRecorder.stop();
      await new Promise(resolve => { mediaRecorder.onstop = resolve; });

      // 转换为 File 并上传
      const blob = new Blob(chunks, { type: 'video/webm' });
      const file = new File([blob], `motion-${Date.now()}.webm`, { type: 'video/webm' });

      setExportProgress(98);
      const url = await uploadFileMultipart(file);

      // 保存到资源中心
      const resolvedProjectId = projectId || nodeData?.projectId || localStorage.getItem('currentProjectId');
      const resolvedEpisodeId = episodeId || nodeData?.episodeId;
      if (resolvedProjectId) {
        try {
          const savePayload = {
            projectId: parseInt(resolvedProjectId),
            url,
            thumbnailUrl: getFirstFrameThumbnail() || url,
            name: `动态分镜 ${new Date().toLocaleString()}`,
            typeCode: 'agent_video',
            source: 'motion_storyboard',
          };
          if (resolvedEpisodeId) {
            savePayload.episodeId = parseInt(resolvedEpisodeId);
          }
          await authenticatedFetch(`${API_HOST}/api/resource/save`, {
            method: 'POST',
            headers: { 'Content-Type': 'application/json' },
            body: JSON.stringify(savePayload),
          });
        } catch (e) {
          console.warn('保存到资源中心失败:', e);
        }
      }

      // 保存到节点数据（含帧数据）
      const thumbnail = getFirstFrameThumbnail();
      const savedFrames = exportFrames();
      onSave?.({
        ...nodeData,
        width,
        height,
        exportedVideoUrl: url,
        exportedAt: new Date().toISOString(),
        thumbnail,
        frameCount: frames.length,
        savedFrames,
      });

      setExportProgress(100);
    } catch (err) {
      console.error('视频导出失败:', err);
      (await appAlert('视频导出失败: ' + err.message));
    } finally {
      setIsExporting(false);
      setExportProgress(0);
    }
  }, [isExporting, saveCurrentFrame, width, height, fps, frames, currentFrameIndex, getFirstFrameThumbnail, exportFrames, nodeData, projectId, episodeId, onSave]);

  // 辅助：加载图片
  function loadImage(src) {
    return new Promise((resolve, reject) => {
      const img = new window.Image();
      img.onload = () => resolve(img);
      img.onerror = reject;
      img.src = src;
    });
  }

  // 关闭编辑器时自动保存帧数据
  const handleClose = useCallback(() => {
    const savedFrames = exportFrames();
    const thumbnail = getFirstFrameThumbnail();
    onSave?.({
      ...nodeData,
      width,
      height,
      thumbnail,
      frameCount: savedFrames.length,
      savedFrames,
    });
    onClose?.();
  }, [exportFrames, getFirstFrameThumbnail, nodeData, width, height, onSave, onClose]);

  // 快捷键
  useEffect(() => {
    if (!isOpen) return;

    const handleKeyDown = (e) => {
      if (e.key === 'Escape') {
        if (isPlaying) handlePause();
        else handleClose();
      }
      if (e.key === ' ') {
        e.preventDefault();
        if (isPlaying) handlePause();
        else handlePlay();
      }
      if (e.key === 'ArrowRight') {
        e.preventDefault();
        nextFrame();
      }
      if (e.key === 'ArrowLeft') {
        e.preventDefault();
        prevFrame();
      }
      if (e.key === 'b') handleToolChange('brush');
      if (e.key === 'p') handleToolChange('pencil');
      if (e.key === 'e') handleToolChange('eraser');
      if (e.key === 'v') handleToolChange('select');
      if (e.key === 'l') handleToolChange('line');
      if (e.key === 'r') handleToolChange('rect');
      if (e.key === 'o') handleToolChange('ellipse');
      if (e.key === 'g') handleToolChange('fill');
      if (e.key === 'k') toggleKeyFrame();
    };

    window.addEventListener('keydown', handleKeyDown);
    return () => window.removeEventListener('keydown', handleKeyDown);
  }, [isOpen, isPlaying, handlePlay, handlePause, handleClose, handleToolChange, nextFrame, prevFrame, toggleKeyFrame]);

  const { mounted, exiting } = useDelayedUnmount(isOpen, 150);
  if (!mounted) return null;

  const accent = theme.colors.accentPrimary;

  return (
    <div
      className={`fixed inset-0 z-[9999] flex flex-col ${exiting ? "animate-out fade-out duration-150" : "animate-in fade-in duration-200"}`}
      style={{ backgroundColor: 'var(--color-bg-primary)' }}
    >
      {/* 顶部工具栏 */}
      <div
        className="flex items-center justify-between px-4 py-2 border-b"
        style={{ borderColor: 'var(--color-border-primary)' }}
      >
        <div className="flex items-center gap-3">
          <span className="text-sm font-bold" style={{ color: 'var(--color-text-primary)' }}>
            动态分镜编辑器
          </span>
          <span
            className="text-xs px-2 py-0.5 rounded-full"
            style={{ backgroundColor: 'var(--color-bg-tertiary)', color: 'var(--color-text-muted)' }}
          >
            {width}×{height} · {fps} fps · {frames.length} 帧
          </span>

          {/* 比例选择 */}
          <div className="flex items-center gap-1 ml-2">
            {ASPECT_RATIOS.map(r => (
              <button
                key={r.label}
                onClick={() => handleAspectRatioChange(r.label)}
                className={`px-2 py-0.5 rounded text-xs transition-all ${
                  aspectRatio === r.label
                    ? 'bg-[var(--color-purple-primary)] text-white'
                    : 'hover:bg-[var(--color-bg-tertiary)]'
                }`}
                style={aspectRatio !== r.label ? { color: 'var(--color-text-muted)' } : {}}
              >
                {r.label}
              </button>
            ))}
          </div>
        </div>

        <div className="flex items-center gap-1">
          {/* 画笔工具组 */}
          {[
            { id: 'brush', icon: <Pencil size={16} />, tip: '画笔 (B)' },
            { id: 'pencil', icon: <PenLine size={16} />, tip: '铅笔 (P)' },
            { id: 'eraser', icon: <Eraser size={16} />, tip: '橡皮擦 (E)' },
          ].map(t => (
            <button
              key={t.id}
              onClick={() => handleToolChange(t.id)}
              className={`p-2 rounded transition-all ${tool === t.id ? 'bg-[var(--color-purple-primary)] text-white' : ''}`}
              title={t.tip}
            >
              {t.icon}
            </button>
          ))}

          <div className="h-5 w-px mx-0.5" style={{ backgroundColor: 'var(--color-border-primary)' }} />

          {/* 形状工具组 */}
          {[
            { id: 'line', icon: <Minus size={16} />, tip: '直线 (L)' },
            { id: 'rect', icon: <RectangleHorizontal size={16} />, tip: '矩形 (R)' },
            { id: 'ellipse', icon: <Circle size={16} />, tip: '椭圆 (O)' },
            { id: 'fill', icon: <PaintBucket size={16} />, tip: '填充 (G)' },
          ].map(t => (
            <button
              key={t.id}
              onClick={() => handleToolChange(t.id)}
              className={`p-2 rounded transition-all ${tool === t.id ? 'bg-[var(--color-purple-primary)] text-white' : ''}`}
              title={t.tip}
            >
              {t.icon}
            </button>
          ))}

          <div className="h-5 w-px mx-0.5" style={{ backgroundColor: 'var(--color-border-primary)' }} />

          {/* 选择 */}
          <button
            onClick={() => handleToolChange('select')}
            className={`p-2 rounded transition-all ${tool === 'select' ? 'bg-[var(--color-purple-primary)] text-white' : ''}`}
            title="选择 (V)"
          >
            <MousePointer size={16} />
          </button>

          <div className="h-5 w-px mx-0.5" style={{ backgroundColor: 'var(--color-border-primary)' }} />

          {/* 颜色 */}
          <input
            type="color"
            value={brushColor}
            onChange={(e) => setBrushColor(e.target.value)}
            className="w-7 h-7 rounded cursor-pointer border-0"
            style={{ padding: 0 }}
            title="选择颜色"
          />

          {/* 快捷色板（功能色：画笔绘制色板，内容语义色，不接入主题变量） */}
          <div className="flex gap-0.5">
            {[
              { c: '#000000', n: '黑色' }, { c: '#ffffff', n: '白色' }, { c: '#ff0000', n: '红色' },
              { c: '#0066ff', n: '蓝色' }, { c: '#00cc44', n: '绿色' }, { c: '#ff9900', n: '橙色' },
              { c: '#9933ff', n: '紫色' },
            ].map(({ c, n }) => (
              <button
                key={c}
                onClick={() => setBrushColor(c)}
                className={`w-5 h-5 rounded-sm border ${brushColor === c ? 'ring-2 ring-[var(--color-purple-primary)]' : ''}`}
                style={{ backgroundColor: c, borderColor: c === '#ffffff' ? '#ccc' : c }}
                title={n}
              />
            ))}
          </div>

          <div className="h-5 w-px mx-0.5" style={{ backgroundColor: 'var(--color-border-primary)' }} />

          {/* 粗细 */}
          <div className="flex items-center gap-1 px-1.5 py-1 rounded-lg" style={{ backgroundColor: 'var(--color-bg-tertiary)' }}>
            <span className="text-xs" style={{ color: 'var(--color-text-muted)' }}>粗细</span>
            <input
              type="range" min="1" max="50" value={brushSize}
              onChange={(e) => setBrushSize(Number(e.target.value))}
              className="w-16 h-1" style={{ accentColor: accent }}
            />
            <span className="text-xs w-5 text-center" style={{ color: 'var(--color-text-primary)' }}>{brushSize}</span>
          </div>

          {/* 透明度 */}
          <div className="flex items-center gap-1 px-1.5 py-1 rounded-lg" style={{ backgroundColor: 'var(--color-bg-tertiary)' }}>
            <span className="text-xs" style={{ color: 'var(--color-text-muted)' }}>透明</span>
            <input
              type="range" min="0.05" max="1" step="0.05" value={brushOpacity}
              onChange={(e) => setBrushOpacity(Number(e.target.value))}
              className="w-14 h-1" style={{ accentColor: accent }}
            />
            <span className="text-xs w-7 text-center" style={{ color: 'var(--color-text-primary)' }}>{Math.round(brushOpacity * 100)}%</span>
          </div>

          <div className="h-5 w-px mx-0.5" style={{ backgroundColor: 'var(--color-border-primary)' }} />

          {/* 洋葱皮 */}
          <button
            onClick={() => setOnionSkinEnabled(!onionSkinEnabled)}
            className={`px-2 py-1 rounded text-xs transition-all ${onionSkinEnabled ? 'bg-[var(--color-purple-primary)] text-white' : ''}`}
            title="洋葱皮 - 显示前后帧半透明叠加"
          >
            洋葱皮
          </button>

          <div className="h-5 w-px mx-0.5" style={{ backgroundColor: 'var(--color-border-primary)' }} />

          {/* 导出 */}
          <button
            onClick={handleExportVideo}
            disabled={isExporting}
            className="px-3 py-1.5 rounded text-xs font-medium transition-all disabled:opacity-50"
            style={{ backgroundColor: accent, color: 'white' }}
            title="导出为视频并保存到资源中心"
          >
            {isExporting ? `导出中 ${exportProgress}%` : '导出视频'}
          </button>

          <button onClick={handleClose} className="p-2 rounded hover:bg-[var(--color-bg-tertiary)] transition-all" title="关闭编辑器 (Esc)">
            <X size={16} />
          </button>
        </div>
      </div>

      {/* 主内容区 */}
      <div className="flex-1 flex overflow-hidden">
        {/* 画布区域 */}
        <div className="flex-1 flex items-center justify-center p-4" style={{ backgroundColor: 'var(--color-bg-secondary)' }}>
          <div
            className="relative overflow-hidden"
            style={{
              width: displaySize.w,
              height: displaySize.h,
              boxShadow: 'var(--shadow-card)',
              borderRadius: 4,
            }}
          >
            {/* 统一缩放容器 */}
            <div
              style={{
                width,
                height,
                position: 'absolute',
                top: 0,
                left: 0,
                transformOrigin: 'top left',
                transform: `scale(${displaySize.scale})`,
              }}
            >
              {/* Fabric Canvas 容器 */}
              <div ref={canvasContainerRef} style={{ width, height, position: 'absolute', top: 0, left: 0 }} />
              {/* 洋葱皮层 - 在 Fabric 上方，不拦截鼠标 */}
              <canvas
                ref={onionCanvasRef}
                width={width}
                height={height}
                style={{
                  width,
                  height,
                  position: 'absolute',
                  top: 0,
                  left: 0,
                  pointerEvents: 'none',
                  zIndex: 10,
                  opacity: onionSkinEnabled ? 1 : 0,
                }}
              />
            </div>
          </div>
        </div>
      </div>

      {/* 底部时间轴 */}
      <div
        className="border-t p-3"
        style={{ borderColor: 'var(--color-border-primary)', backgroundColor: 'var(--color-bg-secondary)' }}
      >
        {/* 播放控制 */}
        <div className="flex items-center gap-2 mb-3">
          <button
            onClick={isPlaying ? handlePause : handlePlay}
            className="p-2 rounded bg-[var(--color-purple-primary)] text-white hover:bg-[color-mix(in_srgb,var(--color-purple-primary)_85%,var(--color-text-primary))] transition-all"
            title={isPlaying ? '暂停 (空格)' : '播放 (空格)'}
          >
            {isPlaying ? <Pause size={16} /> : <Play size={16} />}
          </button>
          <button onClick={handleStop} className="p-2 rounded hover:bg-[var(--color-bg-tertiary)] transition-all" title="停止并回到第一帧">
            <Square size={16} />
          </button>

          <div className="h-5 w-px mx-1" style={{ backgroundColor: 'var(--color-border-primary)' }} />

          <button onClick={prevFrame} className="p-2 rounded hover:bg-[var(--color-bg-tertiary)] transition-all" title="上一帧 (←)">
            <ChevronLeft size={16} />
          </button>
          <span className="text-xs font-mono" style={{ color: 'var(--color-text-primary)' }}>
            {currentFrameIndex + 1} / {frames.length}
          </span>
          <button onClick={nextFrame} className="p-2 rounded hover:bg-[var(--color-bg-tertiary)] transition-all" title="下一帧 (→)">
            <ChevronRight size={16} />
          </button>

          <div className="h-5 w-px mx-1" style={{ backgroundColor: 'var(--color-border-primary)' }} />

          <button onClick={addBlankFrame} className="p-2 rounded hover:bg-[var(--color-bg-tertiary)] transition-all" title="添加空白帧">
            <Plus size={16} />
          </button>
          <button onClick={duplicateFrame} className="p-2 rounded hover:bg-[var(--color-bg-tertiary)] transition-all" title="复制当前帧">
            <Copy size={16} />
          </button>
          <button onClick={deleteFrame} className="p-2 rounded hover:bg-[var(--color-bg-tertiary)] transition-all" title="删除当前帧">
            <Trash2 size={16} />
          </button>
          <button
            onClick={toggleKeyFrame}
            className={`p-2 rounded transition-all ${frames[currentFrameIndex]?.isKeyFrame ? 'text-yellow-500' : ''}`}
            title="标记关键帧 (K)"
          >
            <Star size={16} fill={frames[currentFrameIndex]?.isKeyFrame ? 'currentColor' : 'none'} />
          </button>

          <div className="flex-1" />

          <span className="text-xs" style={{ color: 'var(--color-text-muted)' }}>
            最多 {MAX_FRAMES} 帧
          </span>
        </div>

        {/* 帧缩略图列表 */}
        <div className="flex gap-1 overflow-x-auto pb-2">
          {frames.map((frame, idx) => (
            <button
              key={idx}
              onClick={() => switchToFrame(idx)}
              className={`relative flex-shrink-0 rounded overflow-hidden transition-all ${
                idx === currentFrameIndex ? 'ring-2 ring-[var(--color-purple-primary)]' : ''
              }`}
              style={{
                width: 80,
                height: 60,
                backgroundColor: 'var(--color-bg-tertiary)',
              }}
            >
              {frame.thumbnail ? (
                <img src={frame.thumbnail} alt={`Frame ${idx + 1}`} className="w-full h-full object-cover" />
              ) : (
                <div className="w-full h-full flex items-center justify-center text-xs" style={{ color: 'var(--color-text-muted)' }}>
                  {idx + 1}
                </div>
              )}
              {frame.isKeyFrame && (
                <div className="absolute top-1 right-1">
                  <Star size={12} fill="gold" color="gold" />
                </div>
              )}
            </button>
          ))}
        </div>
      </div>

      <ConfirmDialog
        isOpen={!!pendingAspectRatio}
        onClose={() => setPendingAspectRatio(null)}
        onConfirm={() => {
          const ratioLabel = pendingAspectRatio;
          setPendingAspectRatio(null);
          if (ratioLabel) applyAspectRatio(ratioLabel);
        }}
        title="切换画布比例"
        message={`切换到 ${pendingAspectRatio || ''} 会清空当前所有帧数据，确定继续吗？`}
        confirmLabel="切换并清空"
        cancelLabel="取消"
        variant="danger"
        zIndex={10000}
      />
    </div>
  );
}
